import ToggleDropdown from "@assets/navigation/keyboard_arrow_down.svg";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import ToggleInput from "@components/inputs/ToggleInput";
import EmptyState from "@components/screens/EmptyState";
import { userManagementApis } from "@services/apis/UserManagement";
import { DeleteWhitelistRequest } from "@services/models/UserManagement/delete-whitelist";
import { WhitelistNetworkData } from "@services/models/UserManagement/get-whitelist-data-address";
import { ToggleWhitelistActivityRequest } from "@services/models/UserManagement/toggle-whitelist-activity";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";

interface AddressItemProps {
  networkSymbol: string;
  networkImage: string;
  addresses: {
    id: number;
    active: number;
    address: string;
  }[];
  handleDelete: (id: string) => void;
  handleToggle: (id: string) => void;
  deleteLoadingMap: { [key: string]: boolean };
}

const AddressItem: React.FC<AddressItemProps> = ({
  networkSymbol,
  networkImage,
  addresses,
  handleDelete,
  handleToggle,
  deleteLoadingMap,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="border border-border-subtle rounded-[8px] mb-4">
      <div
        onClick={toggleDropdown}
        className={`bg-bg-dropdown p-[16px] cursor-pointer flex justify-between items-center ${
          isOpen ? "rounded-t-[8px]" : "rounded-[8px]"
        }`}>
        <div className="flex items-center space-x-2">
          <img
            src={networkImage}
            alt={`${networkSymbol} logo`}
            className="w-5 h-5"
          />
          <div className="text-text-base font-semibold text-[14px]">
            {networkSymbol}
          </div>
        </div>

        <img
          src={ToggleDropdown}
          alt="Toggle Dropdown"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        />
      </div>
      {isOpen && (
        <div
          className="rounded-b-[8px]"
          style={{
            maxHeight: isOpen ? "1000px" : "0",
            overflow: "hidden",
            transition: "max-height 0.3s ease-in-out, opacity 0.2s ease-in-out",
            opacity: isOpen ? 1 : 0,
          }}>
          <div className="border-t border-border-subtle"></div>
          {addresses.map(({ id, active, address }, index) => (
            <div className="flex flex-col space-y-[10px]">
              <div
                key={id}
                className={`py-[16px] border-t border-border-table ${
                  index === 0 ? "border-none" : ""
                }`}>
                <div className="flex flex-row justify-between items-start px-[16px]">
                  <p className="text-text-base text-[12px] font-normal mr-1 max-w-full break-words overflow-hidden text-ellipsis">
                    {address}
                  </p>
                  <CopyButton
                    copyText={address}
                    copyMessage="Address copied to clipboard"
                    fillColor="bg-bg-button-secondary"
                    copyIconSize={16}
                    buttonSize="20px"
                    borderRadius="4px"
                  />
                </div>
              </div>
              <div className="flex flex-row w-full justify-between items-center px-[16px] pb-[8px]">
                <ToggleInput
                  isToggled={active === 1 ? true : false}
                  onToggle={() => {
                    handleToggle(id.toString());
                  }}
                />
                <LightCustomButton
                  text="DELETE"
                  borderColor="border-text-status-red"
                  textColor="text-text-status-red"
                  buttonColor="bg-bg-page-body"
                  onClick={() => handleDelete(id.toString())}
                  isActive={true}
                  isButtonLoading={deleteLoadingMap[id.toString()]}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Address = ({ userId }: { userId: number }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [errorAddress, setErrorAddress] = useState<boolean>(false);

  const [whitelistData, setWhitelistData] = useState<WhitelistNetworkData[]>(
    [],
  );
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<{
    [key: string]: boolean;
  }>({});

  const [securitySettingToggleOn, setSecuritySettingToggleOn] =
    useState<boolean>(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [addressToUpdate, setAddressToUpdate] = useState<string | null>(null);
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [addressInfo, setAddressInfo] = useState<{
    networkImage: string;
    networkSymbol: string;
    address: string;
  } | null>(null);

  const fetchData = async ({ updateDelete = false }) => {
    try {
      setLoading(!updateDelete);
      const response = await userManagementApis.getWhitelistDataAddress(userId);
      setWhitelistData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorAddress(true);
    }
  };

  useEffect(() => {
    fetchData({ updateDelete: false });
  }, []);

  const handleDeleteWhitelist = async (
    id: string,
    googleAuthCode?: string,
    emailCode?: string,
  ): Promise<boolean> => {
    const deleteWhitelistRequest: DeleteWhitelistRequest = {
      type: "1",
      id: id,
      userId: userId,
    };

    try {
      setDeleteLoadingMap((prev) => ({
        ...prev,
        [id]: true,
      }));

      const data = await userManagementApis.deleteWhitelist(
        deleteWhitelistRequest,
      );
      handleSuccess({ message: data.msg });
      fetchData({ updateDelete: true });
      return true;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Deleting Whitelisting failed. Please try again.";
      handleError({ message: errorMessage });
      return false;
    } finally {
      setAddressToDelete(null);
      setDeleteLoadingMap((prev) => ({
        ...prev,
        [id]: false,
      }));
    }
  };

  const handleDelete = (
    id: string,
    networkSymbol: string,
    networkImage: string,
    address: string,
  ) => {
    setAddressToUpdate(null);
    setAddressInfo({ networkSymbol, networkImage, address });
    handleDeleteWhitelist(id);
  };

  const handleToggleWhitelist = async (
    id: string,
    googleAuthCode?: string,
    emailCode?: string,
  ) => {
    try {
      const toggleWhitelistRequest: ToggleWhitelistActivityRequest = {
        type: "1",
        id: id,
        userId: userId,
      };
      const data = await userManagementApis.toggleWhitelistActivity(
        toggleWhitelistRequest,
      );
      handleSuccess({ message: data.msg });
      fetchData({ updateDelete: true });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating Whitelisting failed. Please try again.";
      handleError({ message: errorMessage });
      return false;
    } finally {
      setAddressToUpdate(null);
    }
  };

  const handleToggle = (
    id: string,
    networkSymbol: string,
    networkImage: string,
    address: string,
  ) => {
    setAddressToDelete(null);
    setAddressInfo({ networkSymbol, networkImage, address });
    handleToggleWhitelist(id);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden p-[20px]">
      {!loading ? (
        <div>
          {whitelistData.length !== 0 ? (
            <div className="mt-[16px]">
              {whitelistData.map((network) => (
                <AddressItem
                  key={network.networkId}
                  networkImage={network.networkImg}
                  networkSymbol={network.networkSymbol}
                  addresses={network.addresses.map((address) => ({
                    id: address.id,
                    active: address.active,
                    address: address.address,
                  }))}
                  deleteLoadingMap={deleteLoadingMap}
                  handleDelete={(id) =>
                    handleDelete(
                      id,
                      network.networkSymbol,
                      network.networkImg,
                      network.addresses[0].address,
                    )
                  }
                  handleToggle={(id) =>
                    handleToggle(
                      id,
                      network.networkSymbol,
                      network.networkImg,
                      network.addresses[0].address,
                    )
                  }
                />
              ))}
            </div>
          ) : (
            <EmptyState
              text="No Addresses Added to Whitelist"
              subText="No addresses have been added to your whitelist yet. Click the button below to start adding addresses to your whitelist."
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col mt-[16px] space-y-4">
          <Skeleton height={52} />
          <Skeleton height={52} />
        </div>
      )}
    </div>
  );
};

export default Address;
